import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useNavigate } from 'react-router-dom'
import { toBase64 } from 'functions/encoding'
import { resetUserState } from 'redux/slices/user'
import kinesisLogo from "media/kinesis_logo.png"
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function CreateOrganizationView() {

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const organizationLogoInputRef = useRef(null);

    const userId = useSelector(state => state.user.id)
    const email = useSelector(state => state.user.email)

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [organizationName, setOrganizationName] = useState('')
    const [organizationCode, setOrganizationCode] = useState('')
    const [organizationWebsite, setOrganizationWebsite] = useState('')
    const [organizationLogo, setOrganizationLogo] = useState(null);
    const [organizationLogoPreview, setOrganizationLogoPreview] = useState(null);
    const [referralCode, setReferralCode] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [createLoading, setCreateLoading] = useState(false);

    const HandleFormChange = (key, value) => {

        switch (key) {

            case 'firstName':
                var alphabetOnly = value.replace(/[^a-zA-Z]/g, '');
                setFirstName(alphabetOnly)
                setErrorMessage('')
                break

            case 'lastName':
                var alphabetOnly = value.replace(/[^a-zA-Z]/g, '');
                setLastName(alphabetOnly)
                setErrorMessage('')
                break

            case 'organizationName':
                var alphaSpacesAndDigits = value.replace(/[^a-zA-Z0-9\s]/g, '');
                setOrganizationName(alphaSpacesAndDigits)

                const formattedCode = alphaSpacesAndDigits.trim().replace(/\s+/g, '-').toLowerCase() + '-' + moment().format('YYYY');
                setOrganizationCode(formattedCode);

                setErrorMessage('')
                break

            case 'organizationWebsite':
                setOrganizationWebsite(value)
                setErrorMessage('')
                break

            case 'organizationLogo':

                if (value === null || value === undefined) {

                    setOrganizationLogo(null)
                    setOrganizationLogoPreview(null);

                    if (organizationLogoInputRef.current) {
                        organizationLogoInputRef.current.value = '';
                    }

                    setErrorMessage('')

                } else {

                    setOrganizationLogo(value)

                    // Load image preview
                    try {
                        const img = new Image();
                        img.src = URL.createObjectURL(value);
                        img.onload = () => { setOrganizationLogoPreview(img.src); }
                        setErrorMessage('')
                    }
                    catch (error) {
                        console.error(error)
                        setErrorMessage('Failed to load image preview')
                    }

                }

                break

            case 'referralCode':
                setReferralCode(value.toUpperCase())
                setErrorMessage('')
                break

            default:
                console.error(key + ' is not a valid form key')
                break

        }

    }

    const ValidateInputs = () => {
        if (firstName.length === 0) { 
            setErrorMessage('First name is required!'); 
            return false;
        }

        if (lastName.length === 0) { 
            setErrorMessage('Last name is required!'); 
            return false;
        }

        if (organizationName.length === 0) { 
            setErrorMessage('Organization name is required!'); 
            return false;
        }

        if (referralCode.length > 0) {
            if (referralCode !== '8020-COACH') {
                setErrorMessage('Invalid referral code. Please try again');
                return false;
            }
        }

        return true
    }

    const OnCreateOrganization = async () => {

        var inputsValid = ValidateInputs()

        if (inputsValid === false) return

        try {
            setCreateLoading(true)

            // Encode organization logo if provided
            var encodedOrganizationLogo = null
            
            if (organizationLogo !== null) {
                encodedOrganizationLogo = await toBase64(organizationLogo)
            } 
            
            // Otherwise encode Kinesis logo as a default
            if (organizationLogo === null) {
                const response = await fetch(kinesisLogo)
                const blob = await response.blob()
                encodedOrganizationLogo = await toBase64(blob)
            }

            const jsonBody = {
                userId: userId,
                email: email,
                firstName: firstName,
                lastName: lastName,
                organizationName: organizationName,
                organizationCode: organizationCode,
                organizationWebsite: organizationWebsite || null,
                organizationLogo: encodedOrganizationLogo,
                referralCode: referralCode
            }

            // console.log(jsonBody)

            const create_organization_result = await WhichWeightAPI("REPORTING", "POST", "/coach_create_organization", jsonBody);

            // console.log(create_organization_result);

            if (create_organization_result.data.status === "failure") {

                console.error(create_organization_result);

                setCreateLoading(false)

                if (create_organization_result.data.message === "organization-with-code-already-exists") {
                    setErrorMessage('Organization already exists under that name. Try using a different name or go back and join an organization instead.');
                } else {
                    setErrorMessage('Failed to create organization. Please try again. Reach out to support@kinesisintegrated.com for more assistance.');
                }
                
                return;
            }

            setCreateLoading(false)

            dispatch(resetUserState())

            navigate('/home')
        }
        catch (error) {
            console.error(error)
            setCreateLoading(false)
            setErrorMessage('Failed to create organization. Please try again. Reach out to support@kinesisintegrated.com for more assistance.');
        }
    }

    return (
        <ScreenFrame>
            <div className="flex flex-col h-full w-2/3 mx-auto gap-y-4">

                {/* Header */}
                <div className="text-center space-y-2">
                    <div className="flex justify-center mb-2">
                        <img
                            src={kinesisLogo}
                            alt="Kinesis Logo"
                            className="w-32 h-auto"
                        />
                    </div>

                    <h2 className="text-2xl text-gray-900">
                        Create Your Kinesis Organization
                    </h2>

                    <p className="text-base text-gray-600 max-w-xl mx-auto">
                        All information can be updated later in your organization settings
                    </p>
                </div>

                <ContentFrame>
                    <div className="w-full p-6 gap-6 grid grid-cols-2">

                        {/* First Name */}
                        <div>
                            <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                                First name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="first-name"
                                    name="first-name"
                                    type="text"
                                    placeholder="Enter first name here"
                                    autoComplete="given-name"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={firstName}
                                    onChange={(event) => HandleFormChange('firstName', event.target.value)}
                                    maxLength={255}
                                />
                            </div>
                        </div>

                        {/* Last Name */}
                        <div>
                            <label htmlFor="last-name" className="block text-sm font-medium leading-6 text-gray-900">
                                Last name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="last-name"
                                    name="last-name"
                                    type="text"
                                    placeholder="Enter last name here"
                                    autoComplete="family-name"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={lastName}
                                    onChange={(event) => HandleFormChange('lastName', event.target.value)}
                                    maxLength={255}
                                />
                            </div>
                        </div>

                        {/* Organization Name */}
                        <div className="col-span-2">
                            <label htmlFor="organizationName" className="block text-sm font-medium leading-6 text-gray-900">
                                Organization Name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="organizationName"
                                    name="organizationName"
                                    type="organizationName"
                                    placeholder="Enter organization name here"
                                    autoComplete="organizationName"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={organizationName}
                                    onChange={(event) => HandleFormChange('organizationName', event.target.value)}
                                    maxLength={255}
                                />
                            </div>
                        </div>

                        {/* Organization Website */}
                        <div className="col-span-2">
                            <label htmlFor="organizationWebsite" className="block text-sm font-medium leading-6 text-gray-900">
                                Organization Website
                            </label>
                            <div className="mt-2">
                                <input
                                    id="organizationWebsite"
                                    name="organizationWebsite"
                                    type="url"
                                    autoComplete="url"
                                    placeholder="https://www.example.com"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={organizationWebsite}
                                    onChange={(event) => HandleFormChange('organizationWebsite', event.target.value)}
                                    maxLength={255}
                                />
                            </div>
                        </div>

                        {/* Organization Logo */}
                        <div className='col-span-2'>
                            <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                Organization Logo
                            </label>

                            <div className='text-xs text-gray-500 mb-2'>Recommended 256x256 PNG file format</div>

                            <div className='flex items-center gap-x-4'>
                                {organizationLogoPreview === null &&
                                    <div>
                                        <div className='w-20 h-20 border border-gray-200 rounded-full overflow-hidden'>
                                            <img
                                                src={kinesisLogo}
                                                alt="Default organization logo"
                                                className='w-full h-full object-cover'
                                            />
                                        </div>
                                    </div>
                                }

                                {organizationLogoPreview !== null &&
                                    <div>
                                        <div className='w-20 h-20 border border-gray-200 rounded-full overflow-hidden'>
                                            <img
                                                src={organizationLogoPreview}
                                                alt="Selected file preview"
                                                className='w-full h-full object-cover'
                                            />
                                        </div>
                                    </div>
                                }

                                <div className='w-1/2 p-2 bg-gray-50 rounded-lg'>
                                    <input
                                        ref={organizationLogoInputRef}
                                        className='pr-2 w-full text-sm text-gray-900 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-400 file:text-white file:cursor-pointer'
                                        type="file"
                                        accept=".png,.jpg,.jpeg"
                                        onChange={(event) => HandleFormChange('organizationLogo', event.target.files[0])}
                                    />
                                </div>

                                {organizationLogoPreview !== null &&
                                    <div
                                        className='w-6 h-6 flex items-center justify-center cursor-pointer'
                                        onClick={() => HandleFormChange('organizationLogo', null)}
                                    >
                                        <XMarkIcon className='w-6 h-6 text-gray-400' />
                                    </div>
                                }
                            </div>
                        </div>

                        {/* Referral Code */}
                        <div className="col-span-2">
                            <label htmlFor="referral-code" className="block text-sm font-medium leading-6 text-gray-900">
                                Referral Code
                            </label>

                            <div className='text-xs text-gray-500 mb-2'>If you were given a referral code to join Kinesis, enter it here. Reach out to support@kinesisintegrated.com for more assistance.</div>

                            <div className="mt-2">
                                <input
                                    id="referral-code"
                                    name="referral-code"
                                    type="text"
                                    placeholder="Enter referral code here"
                                    autoComplete="given-name"
                                    className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    value={referralCode}
                                    onChange={(event) => HandleFormChange('referralCode', event.target.value)}
                                    maxLength={255}
                                />
                            </div>
                        </div>
                    </div>
                </ContentFrame>

                <div className='text-center text-xs text-gray-500'>
                    By clicking "Create Organization" you agree to the Kinesis Integrated
                    {' '}<a href='https://www.kinesisintegrated.com/privacy' className='text-xs text-orange-500 cursor-pointer hover:underline' target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    {' and '}<a href='https://www.kinesisintegrated.com/terms' className='text-xs text-orange-500 cursor-pointer hover:underline' target="_blank" rel="noopener noreferrer">Terms of Use</a>
                </div>

                {errorMessage !== '' && <div className='text-center text-sm text-red-500 font-bold'>{errorMessage}</div>}

                <div className="flex items-center justify-end gap-x-4">
                    <div className='w-32'>
                        <SolidButton
                            text='Cancel'
                            action={() => navigate('/')}
                            className='shadow-md'
                        />
                    </div>

                    <div className='w-64'>
                        <BrandColorButton
                            text='Create Organization'
                            action={() => OnCreateOrganization()}
                            isLoading={createLoading}
                            className='shadow-md'
                        />
                    </div>
                </div>
            </div>
        </ScreenFrame>
    )
}